// The first-time tutorial (shared/tutorial.ts): the screen dims and one thing
// at a time is lit, with a line or two beside it. A new trainer starts here
// right after choosing their starter; the hunt waits for their first pick, so
// the first fight is one they chose. Skip ends it on any step; Help replays it.
import { S } from './state.ts';
import { renderNamePick } from './namepick.ts';
import { renderLookPick } from './lookpick.ts';
import { openKinhall } from './kinhall.ts';
import { esc, elChip, estimate } from './fmt.ts';
import { tutorialHolds } from '../shared/tutorial.ts';
import { GROUNDS, groundUnlocked } from '../shared/grounds.ts';
import { spriteUrl } from './assets.ts';
import { SPECIES } from '../shared/species.ts';
import type { ClientMsg, GameEvent } from '../shared/protocol.ts';

interface Step {
  /** Candidate targets, first visible one wins; a step with none in view is passed over. */
  at: string[];
  title: string;
  text: string;
  /** The step ends by doing it, not by Next. */
  act?: 'gate' | 'hunt' | 'throw';
  /** Wait this long before lighting it (the first wilds walk in). */
  delay?: number;
  /** On the hunt page, so from the Kinhall it counts as coming. */
  hunt?: boolean;
  /** Said instead when it lights the More menu (a phone); later steps that would light it too are passed over. */
  more?: { title: string; text: string };
}

const nav = (id: string) => [`#top [data-nav="${id}"]:not(.ovf)`, `#bnav a[href="#/${id}"]`];
const MORE = ['#top .navmore > button', '#bnav [data-act="menu-more"]'];

const STEPS: Step[] = [
  { at: ['[data-hall="gate"]'], title: 'The Road', text: 'Out to the wilds.', act: 'gate' },
  { at: ['.stage'], title: 'Choose your first hunt', text: '', act: 'hunt' },
  { at: ['.stage'], title: 'Throw a seal', text: 'Your team fights on its own. Tap a beaten wild to throw a seal at it. If the seal holds, the wild is yours.', act: 'throw', delay: 1600 },
  { at: ['#teamlist', '#teamlist .slot.lead', '#ptabs'], title: 'Team', text: 'Up to five. The top one leads; drag to reorder.', hunt: true },
  // A phone keeps Automation behind the Auto tab.
  { at: ['#auto', '#ptabs [data-id="auto"]'], title: 'Automation', text: 'Throw at throws seals for you. Heal below drinks potions.', hunt: true },
  { at: ['#startersum', '#ptabs [data-id="trainer"]'], title: 'First steps', text: 'Small goals that pay coins and seals.', hunt: true },
  { at: nav('map'), title: 'Map', text: 'New hunts and regions open as you level.' },
  { at: nav('bag'), title: 'Bag', text: 'Seals, potions and everything you find.' },
  { at: [...nav('shop'), ...MORE], title: 'Shop', text: 'Seals run out fast. Buy more here with coins.', more: { title: 'More', text: 'Shop, Market, Quests and your daily reward. Seals run out fast; buy more in the Shop.' } },
  { at: [...nav('market'), ...MORE], title: 'Market', text: 'Buy and sell with other trainers.' },
  { at: ['[data-act="menu-gift"]', ...MORE], title: 'Daily reward', text: 'Claim it every day, along with your quests.' },
];

/** The first step after the hunt is chosen; a replay starts here. */
const FIRST_TOUR = STEPS.findIndex((x) => x.act === 'hunt') + 1;
/** The step for a held trainer on this page: the Road in the Kinhall, the hunt choice anywhere else. */
const heldStep = () => (S.tab === 'kinhall' ? 0 : FIRST_TOUR - 1);
let hallOpened = false;

/** How long the throw step waits before offering Next. */
const THROW_NEXT_MS = 20_000;

let send: (m: ClientMsg) => void = () => {};
let host: HTMLElement | null = null;
let step = -1;
let replay = false;
/** Ended here; the server's word that it is over may still be on its way. */
let ended = false;
let shownAt = 0;
let raf = 0;
/** The step the server last told us about, so a stage change moves us on once. */
let lastStage: string | undefined;

function wanted(): boolean {
  const t = S.trainer;
  if (!t) return false;
  // A brand-new trainer starts in the Kinhall: the first step there points at the Road out.
  if (S.tab && !(S.tab === 'kinhall' && tutorialHolds(t) && !replay)) return false;
  if (document.querySelector('.elsewhere')) return false;
  return replay || (!!t.tutorial && !t.namePick && !t.lookPick && !t.starterPick && !ended);
}

/** Laid out at all; `inView` also wants it on screen. */
function visible(el: Element | null, inView = true): el is HTMLElement {
  if (!(el instanceof HTMLElement) || el.hidden || !el.getClientRects().length) return false;
  const r = el.getBoundingClientRect();
  if (r.width <= 4 || r.height <= 4) return false;
  return !inView || (r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth);
}

function target(s: Step, inView = true): HTMLElement | null {
  for (const sel of s.at) { const el = document.querySelector(sel); if (visible(el, inView)) return el; }
  return null;
}

/** Step i has something to point at that no step before it lit (on a phone, Shop, Market and Daily reward all light More). */
function points(i: number): boolean {
  const el = target(STEPS[i], false);
  return !!el && !STEPS.slice(0, i).some((x) => !x.act && target(x, false) === el);
}

/** Mostly on screen between the top bar and a phone's bottom bar; a step whose target isn't is scrolled to it. */
function wellInView(el: HTMLElement): boolean {
  if (el.closest('#top, #bnav')) return true;
  const r = el.getBoundingClientRect();
  const strip = document.getElementById('strip');
  const top = Math.max(document.getElementById('top')?.getBoundingClientRect().bottom ?? 0, strip && visible(strip) ? strip.getBoundingClientRect().bottom : 0);
  const bn = document.getElementById('bnav');
  const bottom = bn && visible(bn) ? bn.getBoundingClientRect().top : innerHeight;
  return Math.min(r.bottom, bottom) - Math.max(r.top, top) >= Math.min(r.height, bottom - top) * 0.6;
}

/** Lighting the More menu, with words for that. */
const viaMore = (s: Step) => !!s.more && !!target(s, false)?.matches(MORE.join(','));

/** The step to show from `i` on: the first with something to point at (scrolled to on a phone's long hunt view). */
function nextShown(i: number): number {
  for (; i < STEPS.length; i++) if (STEPS[i].act || points(i)) return i;
  return STEPS.length;
}

/** Where the walk-through was, so a reload (a new build, another window) picks it up there. */
const saved = {
  get(): number { try { return Number(localStorage.getItem('mi-tut')) || FIRST_TOUR; } catch { return FIRST_TOUR; } },
  set(i: number) { try { if (i > 0) localStorage.setItem('mi-tut', String(i)); else localStorage.removeItem('mi-tut'); } catch { /* private mode */ } },
};

function finish() {
  if (S.trainer?.tutorial) send({ t: 'tutorial', op: 'done' });
  if (!replay) saved.set(0);
  replay = false; step = -1; ended = true;
  close();
}

function close() {
  cancelAnimationFrame(raf); raf = 0;
  host?.remove(); host = null;
}

function go(i: number) {
  step = nextShown(i);
  if (step >= STEPS.length) return finish();
  if (!replay && step >= FIRST_TOUR) saved.set(step);
  // A replay from Help can land on the hunt scrolled down, the scene tucked under the top bar.
  const el = target(STEPS[step], false);
  if (el && !wellInView(el)) el.scrollIntoView({ block: 'center' });
  shownAt = performance.now();
  draw();
}

function draw() {
  if (!host) return;
  const s = STEPS[step];
  // In the Kinhall the hunt page's targets aren't laid out yet, so its steps count as coming (it read 1 / 7, then 2 / 11).
  const ahead = S.tab === 'kinhall';
  // A replay starts at the throw, so the Road and the hunt choice before it don't count (it read 2 / 10 first).
  const acts = (x: Step, i: number) => !!x.act && (!replay || i >= FIRST_TOUR);
  const shown = STEPS.filter((x, i) => i === step || acts(x, i) || (ahead && i > step && x.hunt) || (!x.act && points(i))).length;
  const n = STEPS.slice(0, step + 1).filter((x, i) => i === step || acts(x, i) || (!x.act && points(i))).length;
  const last = nextShown(step + 1) >= STEPS.length;
  const words = viaMore(s) ? s.more! : s;
  host.classList.toggle('tut-act', s.act === 'gate' || s.act === 'throw');
  host.querySelector('.tut-card')!.innerHTML = `<div class="tut-top"><b>${esc(words.title)}</b><span class="tut-n">${n} / ${shown}</span></div>
    ${s.act === 'hunt' ? huntChoices() : `<p>${esc(words.text)}</p>`}
    <div class="tut-acts"><button class="link" data-tut="skip">Skip tutorial</button>${s.act && s.act !== 'throw' ? '' : `<button class="btn gold small" data-tut="next"${s.act === 'throw' && !replay ? ' hidden' : ''}>${last ? 'Done' : 'Next'}</button>`}</div>`;
  place();
}

/** The hunts open to a new trainer, none chosen yet, each with how your starter matches up against it. */
function huntChoices(): string {
  const t = S.trainer;
  if (!t) return '';
  const lead = S.creatures.get(t.team[0]);
  const open = GROUNDS.filter((g) => groundUnlocked(g, t.level)).slice(0, 4);
  return `<div class="tut-hunts">${open.map((g) => {
    const sp = SPECIES[g.species];
    const e = lead ? estimate(lead, g) : null;
    const vs = !e ? '' : e.mult > 1 ? '<span class="risk-safe">Super effective</span>' : e.mult < 1 ? '<span class="risk-ok">Resisted</span>' : '<span>Neutral</span>';
    return `<button class="tut-hunt" data-tut-hunt="${g.id}"><img src="${spriteUrl(g.species)}" alt=""><span class="tut-hn"><b>${esc(sp.name)}</b> <span class="muted">Lv ${g.level}</span>${sp.elements.map(elChip).join('')}</span><span class="tut-hv">${vs}${e ? ` · ${e.secondsPerKill.toFixed(1)} s/kill` : ''}</span></button>`;
  }).join('')}</div>`;
}

/** Light the target and park the card beside it; runs every frame so it follows layout changes. */
function place() {
  raf = requestAnimationFrame(place);
  if (!host) return;
  // This window lost the game to another one (client/elsewhere.ts): the tutorial waits for "Play here".
  if (document.querySelector('.elsewhere')) { close(); return; }
  const s = STEPS[step];
  const el = target(s);
  const ring = host.querySelector<HTMLElement>('.tut-ring')!;
  const card = host.querySelector<HTMLElement>('.tut-card')!;
  const waiting = !replay && performance.now() - shownAt < (s.delay ?? 0);
  host.classList.toggle('tut-wait', waiting);
  // Scrolled out of view (or gone): the card waits mid-screen, so Next and Skip are always in reach.
  host.classList.toggle('tut-free', !waiting && !el);
  if (waiting) return;
  // The throw step moves on by throwing; Next shows after a while in case no wild goes down in reach.
  if (s.act === 'throw' && !replay) { const nx = card.querySelector<HTMLElement>('[data-tut="next"]'); if (nx && nx.hidden && performance.now() - shownAt > THROW_NEXT_MS) nx.hidden = false; }
  if (!el) { card.style.transform = `translate(${Math.round((innerWidth - card.offsetWidth) / 2)}px, ${Math.round((innerHeight - card.offsetHeight) / 2)}px)`; return; }
  const pad = 6, r = el.getBoundingClientRect();
  const x = Math.max(2, r.left - pad), y = Math.max(2, r.top - pad);
  const w = Math.min(innerWidth - 2, r.right + pad) - x, h = Math.min(innerHeight - 2, r.bottom + pad) - y;
  Object.assign(ring.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px` });
  // The four panes around the light catch stray clicks; on a "do it" step the lit area stays live.
  const [top, left, right, bottom] = host.querySelectorAll<HTMLElement>('.tut-block');
  Object.assign(top.style, { left: '0', top: '0', width: '100%', height: `${y}px` });
  Object.assign(bottom.style, { left: '0', top: `${y + h}px`, width: '100%', height: `${Math.max(0, innerHeight - y - h)}px` });
  Object.assign(left.style, { left: '0', top: `${y}px`, width: `${x}px`, height: `${h}px` });
  Object.assign(right.style, { left: `${x + w}px`, top: `${y}px`, width: `${Math.max(0, innerWidth - x - w)}px`, height: `${h}px` });
  // Card: below, above, right, left of the light, else inside it along the bottom (the big hunt scene).
  const cw = card.offsetWidth, ch = card.offsetHeight, gap = 12, m = 10;
  const clampX = (v: number) => Math.min(innerWidth - cw - m, Math.max(m, v));
  const clampY = (v: number) => Math.min(innerHeight - ch - m, Math.max(m, v));
  let cx: number, cy: number;
  if (s.act === 'hunt') { cx = clampX(x + w / 2 - cw / 2); cy = clampY(y + h / 2 - ch / 2); }
  else if (y + h + gap + ch + m <= innerHeight) { cx = clampX(x + w / 2 - cw / 2); cy = y + h + gap; }
  else if (y - gap - ch >= m) { cx = clampX(x + w / 2 - cw / 2); cy = y - gap - ch; }
  else if (x + w + gap + cw + m <= innerWidth) { cx = x + w + gap; cy = clampY(y + h / 2 - ch / 2); }
  else if (x - gap - cw >= m) { cx = x - gap - cw; cy = clampY(y + h / 2 - ch / 2); }
  else { cx = clampX(x + w / 2 - cw / 2); cy = clampY(y + h - ch - 24); }
  card.style.transform = `translate(${Math.round(cx)}px, ${Math.round(cy)}px)`;
}

function open() {
  host = document.createElement('div');
  host.className = 'tut tut-wait';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Tutorial');
  host.innerHTML = '<div class="tut-block"></div><div class="tut-block"></div><div class="tut-block"></div><div class="tut-block"></div><div class="tut-ring" aria-hidden="true"></div><div class="dialog tut-card" aria-live="polite"></div>';
  host.addEventListener('click', (e) => {
    const h = (e.target as HTMLElement).closest<HTMLElement>('[data-tut-hunt]');
    if (h) { send({ t: 'ground', id: h.dataset.tutHunt! }); return go(step + 1); }
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-tut]');
    if (!b) return;
    if (b.dataset.tut === 'skip') return finish();
    go(step + 1);
  });
  document.body.appendChild(host);
}

function onKey(e: KeyboardEvent) {
  if (!host) return;
  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(); }
  else if ((e.key === 'Enter' || e.key === 'ArrowRight') && (!STEPS[step].act || (STEPS[step].act === 'throw' && (replay || performance.now() - shownAt > THROW_NEXT_MS)))) { e.preventDefault(); e.stopPropagation(); go(step + 1); }
}

export function mountTutorial(sender: (m: ClientMsg) => void) {
  send = sender;
  document.addEventListener('keydown', onKey, true);
}

/**
 * Hunt events: the first seal thrown ends the throw step. Not in a replay: there Throw at is usually on, a seal
 * flies within a second and the first card was gone before it could be read (2026-10-25); Next ends it instead.
 */
/** True while the "Throw a seal" step shows: a tap anywhere on the scene throws at the nearest beaten wild (client/catch.ts). */
export const throwStepShowing = () => !!host && STEPS[step]?.act === 'throw';

export function tutorialEvents(evs: GameEvent[]) {
  if (host && !replay && STEPS[step]?.act === 'throw' && evs.some((e) => e.k === 'throw')) go(step + 1);
}

/** Replay from Help: the walk-through without the held hunt. */
export function replayTutorial() {
  replay = true; ended = false;
  close();
  step = -1;
}

/** Called from render(): opens, follows or closes the tutorial. */
export function renderTutorial() {
  renderNamePick(send);
  renderLookPick(send);
  const t = S.trainer;
  const stage = t?.tutorial;
  // Until the first hunt is chosen there is no hunt to show: the scene stays empty and quiet.
  const hold = !!t && tutorialHolds(t);
  if (document.documentElement.classList.contains('tut-hold') !== hold) document.documentElement.classList.toggle('tut-hold', hold);
  // Name, look and starter come first: no team shows until the starter is chosen.
  const pre = !!t && !!(t.namePick || t.lookPick || t.starterPick);
  if (document.documentElement.classList.contains('tut-pre') !== pre) document.documentElement.classList.toggle('tut-pre', pre);
  if (hold && !hallOpened) { hallOpened = true; if (!S.tab) openKinhall(); }
  if (!wanted()) { if (host) close(); if (!t?.tutorial && !replay) step = -1; lastStage = stage; return; }
  if (!host) {
    open();
    go(hold && !replay ? heldStep() : step >= 0 ? step : replay ? FIRST_TOUR : saved.get());
  } else if (hold && step < FIRST_TOUR && step !== heldStep()) {
    // Through the Road (or back to the hall): follow the page.
    go(heldStep());
  } else if (lastStage === 'hunt' && stage !== 'hunt' && STEPS[step]?.act === 'hunt') {
    // The hunt started some other way (the Map, another tab): move on.
    go(step + 1);
  }
  lastStage = stage;
}
